<script lang="ts">
  /**
   * "Did the geyser change after the timer?" — a before/after pair on one axis.
   *
   * The two charts share a single vertical scale, computed across both series.
   * That is the whole shape: two charts each scaled to their own peak would show
   * a halving and a doubling as identical pictures, which is exactly the mistake
   * this question is asked to avoid. `max` is passed to both `TrendLine`s and
   * neither is allowed to compute its own.
   *
   * The windows are the same length by construction — the server clamps the
   * after-window to what exists and cuts the before-window to match — so the two
   * halves are comparable without the reader having to notice that one of them
   * is shorter.
   *
   * Each half carries its own mean as a dashed rule, and the means come from the
   * server alongside the verdict. Re-deriving them here would risk a chart that
   * quietly contradicts the sentence above it.
   */
  import { fig, nice, type BeforeAfterAnswer } from "../answers";
  import TrendLine from "./TrendLine.svelte";

  let { a, tone = "ok" }: { a: BeforeAfterAnswer; tone?: "ok" | "watch" | "thin" } = $props();

  /** One scale for both halves. Nulls are gaps, not zeroes, so they are dropped. */
  const shared = $derived(
    Math.max(...[...a.before, ...a.after].map((p) => p.value ?? 0), 0),
  );

  const show = (v: number | null) => (v === null ? "—" : `${fig(v)}${a.unit}`);
</script>

<div class="b">
  <div class="b__pair">
    <section class="b__half">
      <header class="b__head">
        <span class="lb">before · {a.before.length} days</span>
        <span class="b__mean num">mean {show(a.beforeMean)}</span>
      </header>
      <TrendLine
        points={a.before}
        tone="thin"
        unit={a.unit}
        max={shared}
        height={128}
        label={a.metric}
        reference={a.beforeMean === null ? null : { value: a.beforeMean, label: "mean before" }}
      />
    </section>

    <section class="b__half">
      <header class="b__head">
        <span class="lb">after · {a.after.length} days</span>
        <span class="b__mean num">mean {show(a.afterMean)}</span>
      </header>
      <TrendLine
        points={a.after}
        {tone}
        unit={a.unit}
        max={shared}
        height={128}
        label={a.metric}
        reference={a.afterMean === null ? null : { value: a.afterMean, label: "mean after" }}
      />
    </section>
  </div>

  <p class="b__foot">
    {nice(a.entity ?? "")}, {a.metric}. Both halves are drawn to the same scale — peak {shared > 0 ? fig(shared) : "—"}{a.unit} — so the two pictures can be compared directly.
    {#if a.pivot}
      The split is {a.pivot}, taken from {a.pivotFrom ?? "the history"}.
    {/if}
  </p>
</div>

<style>
  .b {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
  .b__pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--sp-4);
  }
  /* Side by side is the point — stacked, the reader compares two pictures from
     memory. Below this width side-by-side is worse than the memory test. */
  @container panel (max-width: 560px) {
    .b__pair {
      grid-template-columns: 1fr;
    }
  }
  .b__half {
    min-width: 0;
  }
  .b__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-2);
    padding-bottom: var(--sp-2);
  }
  .b__mean {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--tx2);
  }
  .b__foot {
    margin: 0;
    font-size: var(--fs-micro);
    color: var(--mut);
    line-height: 1.5;
    max-width: 74ch;
  }
</style>
